import React, { useState, useEffect, useRef } from 'react';
import type Vapi from '@vapi-ai/web';
import { motion } from 'framer-motion';
import { Mic, Volume2, PhoneOff } from 'lucide-react';
import { trackUserEngagement } from '../lib/analytics';
import { useVapi } from '../contexts/VapiContext';

interface VapiWidgetProps {
  apiKey: string;
  assistantId: string;
  config?: Record<string, unknown>;
  buttonText?: string;
  className?: string;
  onConnectionChange?: (isConnected: boolean) => void;
}

const VapiWidget: React.FC<VapiWidgetProps> = ({
  apiKey,
  assistantId,
  buttonText = "Talk to the Founder (AI)",
  className = "",
  onConnectionChange
}) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const requested = useRef(false);
  const [vapi, setVapi] = useState<Vapi | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showSpeechBubble, setShowSpeechBubble] = useState(false);
  const speechBubbleIndex = 0;
  const { registerTrigger } = useVapi();

  const speechMessages = [
    "Available 24/7 • Click to chat",
    "Test our AI now"
  ];

  useEffect(() => {
    if (!shouldLoad || !apiKey) return;
    let cancelled = false;
    let vapiInstance: Vapi | null = null;
    const setup = async () => {
      const { default: VapiClient } = await import('@vapi-ai/web');
      if (cancelled) return;
      vapiInstance = new VapiClient(apiKey);
      setVapi(vapiInstance);

    // Event listeners
    vapiInstance.on('call-start', () => {
      console.log('Call started');
      trackUserEngagement('vapi_call_started');
      setIsConnected(true);
      setShowSpeechBubble(false); // Hide bubble when call starts
      onConnectionChange?.(true);
    });

    vapiInstance.on('call-end', () => {
      console.log('Call ended');
      trackUserEngagement('vapi_call_ended');
      setIsConnected(false);
      setIsSpeaking(false);
      onConnectionChange?.(false);
    });

    vapiInstance.on('speech-start', () => {
      console.log('Assistant started speaking');
      setIsSpeaking(true);
    });

    vapiInstance.on('speech-end', () => {
      console.log('Assistant stopped speaking');
      setIsSpeaking(false);
      // Removed auto-end logic - let user control when to end the call
    });

    vapiInstance.on('error', (error) => {
      console.error('Vapi error:', error);
    });

    };
    setup().catch(() => { if (!cancelled) { setLoadError(true); setShouldLoad(false); requested.current = false; } });
    return () => { cancelled = true; vapiInstance?.stop(); };
  }, [apiKey, onConnectionChange, shouldLoad]);

  const startCall = () => {
    if (!apiKey || !assistantId) { setLoadError(true); return; }
    setLoadError(false);
    if (vapi) {
      vapi.start(assistantId);
    } else {
      requested.current = true;
      setShouldLoad(true);
    }
  };

  useEffect(() => {
    registerTrigger(startCall);
    if (vapi && requested.current) { requested.current = false; vapi.start(assistantId); }
  }, [vapi, assistantId, registerTrigger]);

  const endCall = () => {
    if (vapi) {
      vapi.stop();
    }
  };

  if (isConnected) {
    // When call is active, show the call status and prominent end call button
    return (
      <div className={`relative w-full ${className} touch-spacing`}>
        <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4 w-full">
          {/* Call Status Display */}
          <div className="flex items-center space-x-2 px-3 py-2 backdrop-blur-sm border border-blue-500/40 rounded-lg min-w-0 flex-shrink min-h-[48px]" style={{ backgroundColor: 'rgba(13, 13, 13, 0.9)' }}>
            {/* Status Icon */}
            <div className="flex items-center space-x-2 min-w-0">
              {isSpeaking ? (
                <div className="w-6 h-6 bg-[#d4a10d]/15 rounded-full border border-[#d4a10d]/40 flex items-center justify-center flex-shrink-0">
                  <Volume2 className="w-3 h-3 text-[#d4a10d]" />
                </div>
              ) : (
                <div className="w-6 h-6 bg-[#d4a10d]/15 rounded-full border border-[#d4a10d]/40 flex items-center justify-center flex-shrink-0">
                  <Mic className="w-3 h-3 text-[#d4a10d]" />
                </div>
              )}

              {/* Status Text */}
              <div className="flex flex-col min-w-0">
                <span className={`font-tech text-xs font-medium truncate ${
                  isSpeaking ? 'text-[#d4a10d]' : 'text-[#d4a10d]'
                }`}>
                  {isSpeaking ? 'AI Speaking' : 'Listening...'}
                </span>
                <span className="font-tech text-[10px] text-gray-400 truncate">
                  Call Active
                </span>
              </div>
            </div>
          </div>

          {/* Prominent End Call Button */}
          <button
            onClick={endCall}
            className="group relative overflow-hidden flex-shrink-0 min-h-[48px] min-w-[48px]"
            aria-label="End call"
          >
            {/* Red glow background */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-500 to-red-600 rounded-lg opacity-60 group-hover:opacity-80 blur-sm transition-all duration-300" />
            
            {/* Button content */}
            <div className="relative px-3 sm:px-4 py-3 rounded-lg border border-red-500/40 group-hover:border-red-500/60 transition-all duration-300 min-h-[48px] flex items-center" style={{ backgroundColor: '#0d0d0d' }}>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <PhoneOff className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span className="brand-title text-red-400 text-sm font-semibold truncate">
                  End Call
                </span>
              </div>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // When call is not active, show the start button
  return (
    <div className={`relative ${className} touch-spacing`}>
      {loadError && <p role="alert" className="text-sm text-red-700 bg-white p-2 rounded-lg max-w-xs">The demo couldn’t load. Please try again.</p>}
      {shouldLoad && !vapi && <p role="status" className="text-sm text-gray-600 bg-white p-2 rounded-lg">Loading the voice demo…</p>}
      {/* Professional Speech Bubble - Mobile Optimized */}
      {showSpeechBubble && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 8 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="absolute bottom-20 right-0 mb-2"
        >
          <div className="relative max-w-[220px]">
            {/* Clean Modern Speech Bubble */}
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 px-5 py-3.5 transition-all duration-200 hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)]">
              {/* Subtle gradient accent */}
              <div className="absolute inset-0 bg-gradient-to-br from-black/[0.02] to-transparent rounded-2xl pointer-events-none" />

              <p className="relative text-gray-900 text-sm font-semibold leading-relaxed tracking-tight">
                {speechMessages[speechBubbleIndex]}
              </p>

              {/* Clean Speech Bubble Tail */}
              <div className="absolute bottom-0 right-8 transform translate-y-full">
                <div className="w-0 h-0 border-l-[7px] border-r-[7px] border-t-[7px] border-l-transparent border-r-transparent border-t-white drop-shadow-sm"></div>
              </div>
            </div>

            {/* Clean Status Indicator */}
            <div className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 bg-[#d4a10d] rounded-full border-2 border-white shadow-sm">
              <div className="absolute inset-0 bg-[#d4a10d] rounded-full animate-pulse opacity-60" />
            </div>
          </div>
        </motion.div>
      )}

      <button
        onClick={startCall}
        className="group relative flex items-center justify-center w-16 h-16 sm:w-14 sm:h-14 rounded-full border-2 border-black/50 hover:border-black/70 transition-all duration-300 cursor-pointer active:scale-95 hover:scale-105 shadow-xl hover:shadow-2xl bg-gradient-to-br from-black via-gray-900 to-black hover:from-gray-900 hover:via-black hover:to-gray-900"
        onMouseEnter={() => !isConnected && window.innerWidth >= 640 && setShowSpeechBubble(true)}
        onMouseLeave={() => !isConnected && window.innerWidth >= 640 && setTimeout(() => setShowSpeechBubble(false), 1500)}
        onTouchStart={() => !isConnected && setShowSpeechBubble(true)}
        aria-label={buttonText}
        title={buttonText}
      >
        {/* Enhanced Icon */}
        <div className="w-7 h-7 sm:w-6 sm:h-6 rounded-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center group-hover:from-white/15 group-hover:to-white/10 transition-all duration-300 shadow-inner border border-white/10">
          <div className="w-2.5 h-2.5 sm:w-2 sm:h-2 rounded-full bg-white group-hover:bg-white/90 transition-all duration-300 shadow-lg animate-pulse"></div>
        </div>

        {/* Enhanced Pulse animation */}
        <div className="absolute inset-0 rounded-full border-2 border-white/20 animate-pulse shadow-lg"></div>
        <div className="absolute -inset-1 rounded-full border border-white/10 animate-pulse" style={{ animationDelay: '0.5s' }}></div>

        {/* Enhanced Glow effect on hover */}
        <div className="absolute -inset-4 sm:-inset-3 bg-black/30 rounded-full opacity-0 group-hover:opacity-100 blur-xl transition-all duration-500"></div>
        <div className="absolute -inset-2 sm:-inset-1 bg-black/20 rounded-full opacity-0 group-hover:opacity-70 blur-md transition-all duration-300"></div>

        {/* Hidden text for accessibility */}
        <span className="sr-only">
          Talk to the Founder (AI)
        </span>
      </button>
    </div>
  );
};

export default VapiWidget;
